import dayjs from 'dayjs'
import { Form, Formik } from 'formik'
import { FC, useState } from 'react'
import Stack from 'react-bootstrap/Stack'
import { FormattedMessage } from 'react-intl'

import { GenderEnum, ID } from '@models/base'
import { Option } from '@models/option'

import { Button } from '@components/Button'
import { AdministrativeDivisionInputField } from '@components/Form/AdministrativeDivisionInput'
import { DatePickerInputField } from '@components/Form/DatePicker'
import { GenderRadioButtonField } from '@components/Form/GenderRadioButton'
import { InputField } from '@components/Form/Input'
import { PhoneInputField } from '@components/Form/PhoneInput'
import { PostCodeInputField } from '@components/Form/PostalCodeInput'
import { RelationTypeSelectField } from '@components/Form/RelationTypeSelectField/RelationTypeSelectField'
import { ToggleSwitchField } from '@components/Form/ToggleSwitch'
import { FormControl } from '@components/FormControl'
import { FormSection } from '@components/FormSection'
import { ScrollIntoView } from '@components/ScrollIntoView'

import { CountrySelectField } from '@modules/country/components/CountrySelectField'

import AxiosUtil from '@/utils/axiosUtil'
import FormUtil, { FormDisabledFlags, FormShape } from '@/utils/formUtil'

import { PatientRelativeFormLivingToggleSwitch } from './field/PatientRelativeFormLivingToggleSwitch'
import { PatientRelativeFormNricAddressField } from './field/PatientRelativeFormNricAddressField'
import { PartientRelativeFormPatientSelectField } from './field/PatientRelativeFormPatientSelectField'
import Col from 'react-bootstrap/Col'
import Row from 'react-bootstrap/Row'

type PatientRelativeFormShape = FormShape<{
  patient_id: ID
  first_name: string
  last_name: string
  gender: GenderEnum
  birth_date: string
  is_default: true
  related_patient_id: Option
  relation_type: Option
  nric_number: string
  family_card_number: string
  passport_number: string
  nric_country: Option
  nric_first_level_administrative_division_id: Option
  nric_second_level_administrative_division_id: Option
  nric_third_level_administrative_division_id: Option
  nric_fourth_level_administrative_division_id: Option
  nric_postal_code: string
  nric_address_1: string
  nric_address_2: string
  nric_rt: string
  nric_rw: string
  living_country: Option
  living_first_level_administrative_division_id: Option
  living_second_level_administrative_division_id: Option
  living_third_level_administrative_division_id: Option
  living_fourth_level_administrative_division_id: Option
  living_postal_code: string
  living_address_1: string
  living_address_2: string
  living_rt: string
  living_rw: string
  mobile_phone_number: string
  home_phone_number: string
  business_phone_number: string
  created_at: string
  updated_at: string
  registered_patient: boolean
  is_same_address: boolean
}>

type PatientRelativeFormProps = {
  patientId: ID
  initialValues?: PatientRelativeFormShape
  disabledFields?: Partial<FormDisabledFlags<PatientRelativeFormShape>>
  onSubmit: (values: PatientRelativeFormShape) => Promise<void>
  onCancel: () => void
}

const PatientRelativeForm: FC<PatientRelativeFormProps> = ({
  initialValues,
  patientId,
  disabledFields,
  onSubmit,
  onCancel,
}) => {
  const [error, setError] = useState<string>()

  const today = dayjs().toTzISOString()

  return (
    <ScrollIntoView<HTMLFormElement>>
      {({ elementRef, executeScroll }) => (
        <Formik<PatientRelativeFormShape>
          validateOnChange={false}
          initialValues={{
            ...initialValues,
          }}
          onSubmit={async (values, { setSubmitting, setErrors }) => {
            try {
              setSubmitting(true)

              await onSubmit(values)
            } catch (err) {
              if (AxiosUtil.isAxiosError(err)) {
                const errors = FormUtil.parseErrorResponse(err.response?.data.errors)
                if (errors) {
                  setErrors(errors)
                } else {
                  setError(err.response?.data.message)
                }
              } else {
                setError(String(err))
              }
              executeScroll()
            } finally {
              setSubmitting(false)
            }
          }}
        >
          {({ values, handleSubmit, isSubmitting, dirty, errors, setFieldValue }) => {
            const isInputByPatientData = values.registered_patient === true
            const isSameAddress = values.is_same_address === true

            return (
              <Form
                ref={elementRef}
                onSubmit={handleSubmit}
                noValidate
              >
                {error && <div className="alert alert-danger d-flex align-items-center p-5 mb-10">{error}</div>}

                {disabledFields?.registered_patient ? null : (
                  <FormSection
                    title={<FormattedMessage id="patient_relative.form.relative.title" />}
                    description={<FormattedMessage id="patient_relative.form.relative.description" />}
                  >
                    <ToggleSwitchField
                      accept="boolean"
                      name="registered_patient"
                    />
                  </FormSection>
                )}

                <FormSection
                  isEndOfChild={isInputByPatientData}
                  title={<FormattedMessage id="patient.form_general_title" />}
                  description={<FormattedMessage id="patient.form_general_description" />}
                >
                  {isInputByPatientData && (
                    <FormControl>
                      <FormControl.Label>
                        <FormattedMessage id="vocabulary.patient" />
                      </FormControl.Label>

                      <PartientRelativeFormPatientSelectField
                        name="related_patient_id"
                        patientId={patientId}
                        isDisabled={disabledFields?.related_patient_id}
                      />
                      <FormControl.ErrorMessage message={errors.related_patient_id} />
                    </FormControl>
                  )}

                  {!isInputByPatientData && (
                    <>
                      <Row>
                        <Col xl={6}>
                          <FormControl>
                            <FormControl.Label isRequired>
                              <FormattedMessage id="vocabulary.first_name" />
                            </FormControl.Label>
                            <InputField
                              name="first_name"
                              isDisabled={disabledFields?.first_name}
                            />
                            <FormControl.ErrorMessage message={errors.first_name} />
                          </FormControl>
                        </Col>

                        <Col xl={6}>
                          <FormControl>
                            <FormControl.Label>
                              <FormattedMessage id="vocabulary.last_name" />
                            </FormControl.Label>
                            <InputField
                              name="last_name"
                              isDisabled={disabledFields?.last_name}
                            />
                            <FormControl.ErrorMessage message={errors.last_name} />
                          </FormControl>
                        </Col>
                      </Row>

                      <Row>
                        <Col xl={6}>
                          <FormControl>
                            <FormControl.Label isRequired>
                              <FormattedMessage id="vocabulary.birth_date" />
                            </FormControl.Label>
                            <DatePickerInputField
                              name="birth_date"
                              maxDate={today}
                              disabled={disabledFields?.birth_date}
                            />
                            <FormControl.ErrorMessage message={errors.birth_date} />
                          </FormControl>
                        </Col>

                        <Col xl={6}>
                          <FormControl>
                            <FormControl.Label isRequired>
                              <FormattedMessage id="vocabulary.gender" />
                            </FormControl.Label>
                            <GenderRadioButtonField
                              name="gender"
                              isDisabled={disabledFields?.gender}
                            />
                            <FormControl.ErrorMessage message={errors.gender} />
                          </FormControl>
                        </Col>
                      </Row>
                    </>
                  )}

                  <Row>
                    <Col xl={12}>
                      <FormControl>
                        <FormControl.Label isRequired>
                          <FormattedMessage id="patient.relation_type" />
                        </FormControl.Label>

                        <RelationTypeSelectField
                          name="relation_type"
                          isDisabled={disabledFields?.relation_type}
                        />
                        <FormControl.ErrorMessage message={errors.relation_type} />
                      </FormControl>
                    </Col>
                  </Row>
                </FormSection>

                {!isInputByPatientData && (
                  <>
                    <FormSection
                      title={<FormattedMessage id="patient.nric.title" />}
                      description={<FormattedMessage id="patient.nric.description" />}
                    >
                      <Row>
                        <Col xl={12}>
                          <FormControl>
                            <FormControl.Label>
                              <FormattedMessage id="patient.nik" />
                            </FormControl.Label>
                            <InputField
                              name="nric_number"
                              isDisabled={disabledFields?.nric_number}
                            />
                            <FormControl.ErrorMessage message={errors.nric_number} />
                          </FormControl>
                        </Col>

                        <Col xl={6}>
                          <FormControl>
                            <FormControl.Label>
                              <FormattedMessage id="patient.kk" />
                            </FormControl.Label>
                            <InputField
                              name="family_card_number"
                              isDisabled={disabledFields?.family_card_number}
                            />
                            <FormControl.ErrorMessage message={errors.family_card_number} />
                          </FormControl>
                        </Col>

                        <Col xl={6}>
                          <FormControl>
                            <FormControl.Label>
                              <FormattedMessage id="passport_number" />
                            </FormControl.Label>
                            <InputField
                              name="passport_number"
                              isDisabled={disabledFields?.passport_number}
                            />
                            <FormControl.ErrorMessage message={errors.passport_number} />
                          </FormControl>
                        </Col>
                      </Row>

                      <PatientRelativeFormNricAddressField />
                    </FormSection>

                    <FormSection
                      title={<FormattedMessage id="patient.address.title" />}
                      description={<FormattedMessage id="patient.address.description" />}
                    >
                      <FormControl>
                        <FormControl.Label>
                          <FormattedMessage id="same_as_ktp" />
                        </FormControl.Label>
                        <PatientRelativeFormLivingToggleSwitch name="is_same_address" />
                      </FormControl>

                      <FormControl>
                        <FormControl.Label>
                          <FormattedMessage id="patient.address" />
                        </FormControl.Label>
                        <Stack gap={3}>
                          <InputField
                            name="living_address_1"
                            isDisabled={isSameAddress || disabledFields?.living_address_1}
                          />
                          <InputField
                            name="living_address_2"
                            isDisabled={isSameAddress || disabledFields?.living_address_2}
                          />
                        </Stack>
                        <FormControl.ErrorMessage message={errors.living_address_1 || errors.living_address_2} />
                      </FormControl>

                      <Row sm={2}>
                        <Col
                          md={6}
                          lg={4}
                          xl={3}
                        >
                          <FormControl>
                            <FormControl.Label>
                              <FormattedMessage id="vocabulary.rt" />
                            </FormControl.Label>
                            <InputField
                              name="living_rt"
                              isDisabled={isSameAddress || disabledFields?.living_rt}
                            />
                            <FormControl.ErrorMessage message={errors.living_rt} />
                          </FormControl>
                        </Col>
                        <Col
                          md={6}
                          lg={4}
                          xl={3}
                        >
                          <FormControl>
                            <FormControl.Label>
                              <FormattedMessage id="vocabulary.rw" />
                            </FormControl.Label>
                            <InputField
                              name="living_rw"
                              isDisabled={isSameAddress || disabledFields?.living_rw}
                            />
                            <FormControl.ErrorMessage message={errors.living_rw} />
                          </FormControl>
                        </Col>
                      </Row>

                      <FormControl>
                        <FormControl.Label>
                          <FormattedMessage id="country" />
                        </FormControl.Label>

                        <CountrySelectField
                          name="living_country"
                          isDisabled={isSameAddress || disabledFields?.living_country}
                          onChange={() => {
                            setFieldValue('living_first_level_administrative_division_id', undefined)
                          }}
                        />
                        <FormControl.ErrorMessage message={errors.living_country} />
                      </FormControl>

                      <FormControl>
                        <FormControl.Label>
                          <FormattedMessage id="province" />
                        </FormControl.Label>

                        <AdministrativeDivisionInputField
                          name="living_first_level_administrative_division_id"
                          countryId={values.living_country?.data?.id}
                          isDisabled={isSameAddress || disabledFields?.living_first_level_administrative_division_id}
                          onChange={() => {
                            setFieldValue('living_second_level_administrative_division_id', undefined)
                          }}
                        />
                        <FormControl.ErrorMessage message={errors.living_first_level_administrative_division_id} />
                      </FormControl>

                      <FormControl>
                        <FormControl.Label>
                          <FormattedMessage id="city" />
                        </FormControl.Label>

                        <AdministrativeDivisionInputField
                          name="living_second_level_administrative_division_id"
                          countryId={values.living_country?.data?.id}
                          parentId={values.living_first_level_administrative_division_id?.value ?? null}
                          isDisabled={isSameAddress || disabledFields?.living_second_level_administrative_division_id}
                          onChange={() => {
                            setFieldValue('living_third_level_administrative_division_id', undefined)
                          }}
                        />
                        <FormControl.ErrorMessage message={errors.living_second_level_administrative_division_id} />
                      </FormControl>

                      <FormControl>
                        <FormControl.Label>
                          <FormattedMessage id="district" />
                        </FormControl.Label>

                        <AdministrativeDivisionInputField
                          name="living_third_level_administrative_division_id"
                          countryId={values.living_country?.data?.id}
                          parentId={values.living_second_level_administrative_division_id?.value ?? null}
                          isDisabled={isSameAddress || disabledFields?.living_third_level_administrative_division_id}
                          onChange={() => {
                            setFieldValue('living_fourth_level_administrative_division_id', undefined)
                          }}
                        />
                        <FormControl.ErrorMessage message={errors.living_third_level_administrative_division_id} />
                      </FormControl>

                      <FormControl>
                        <FormControl.Label>
                          <FormattedMessage id="sub_district" />
                        </FormControl.Label>

                        <AdministrativeDivisionInputField
                          name="living_fourth_level_administrative_division_id"
                          countryId={values.living_country?.data?.id}
                          parentId={values.living_third_level_administrative_division_id?.value ?? null}
                          isDisabled={isSameAddress || disabledFields?.living_fourth_level_administrative_division_id}
                        />
                        <FormControl.ErrorMessage message={errors.living_fourth_level_administrative_division_id} />
                      </FormControl>

                      <FormControl>
                        <FormControl.Label>
                          <FormattedMessage id="postal_code" />
                        </FormControl.Label>
                        <PostCodeInputField
                          name="living_postal_code"
                          isDisabled={isSameAddress || disabledFields?.living_postal_code}
                        />
                        <FormControl.ErrorMessage message={errors.living_postal_code} />
                      </FormControl>
                    </FormSection>

                    <FormSection
                      isEndOfChild
                      title={<FormattedMessage id="patient.contact.title" />}
                      description={<FormattedMessage id="patient.contact.description" />}
                    >
                      <Row>
                        <Col xl={6}>
                          <FormControl>
                            <FormControl.Label>
                              <FormattedMessage id="patient.mobile_phone_number" />
                            </FormControl.Label>
                            <PhoneInputField
                              name="mobile_phone_number"
                              disabled={disabledFields?.mobile_phone_number}
                            />
                            <FormControl.ErrorMessage message={errors.mobile_phone_number} />
                          </FormControl>
                        </Col>

                        <Col xl={6}>
                          <FormControl>
                            <FormControl.Label>
                              <FormattedMessage id="patient.home_phone_number" />
                            </FormControl.Label>
                            <PhoneInputField
                              name="home_phone_number"
                              disabled={disabledFields?.home_phone_number}
                            />
                            <FormControl.ErrorMessage message={errors.home_phone_number} />
                          </FormControl>
                        </Col>

                        <Col xl={6}>
                          <FormControl>
                            <FormControl.Label>
                              <FormattedMessage id="patient.business_home_number" />
                            </FormControl.Label>
                            <PhoneInputField
                              name="business_phone_number"
                              disabled={disabledFields?.business_phone_number}
                            />
                            <FormControl.ErrorMessage message={errors.business_phone_number} />
                          </FormControl>
                        </Col>
                      </Row>
                    </FormSection>
                  </>
                )}

                <div className="d-flex justify-content-end mt-5 pt-6 border-top gap-3">
                  <Button
                    theme="light"
                    onClick={onCancel}
                  >
                    <FormattedMessage id="form.discard" />
                  </Button>
                  <Button
                    type="submit"
                    theme="primary"
                    disabled={isSubmitting || !dirty}
                    isLoading={isSubmitting}
                  >
                    <FormattedMessage id="form.submit" />
                  </Button>
                </div>
              </Form>
            )
          }}
        </Formik>
      )}
    </ScrollIntoView>
  )
}

export type { PatientRelativeFormShape, PatientRelativeFormProps }

export { PatientRelativeForm }
